import { AccountBalanceOutlined, CalendarMonthOutlined, HourglassTop, Login, TaskAlt } from '@mui/icons-material';
import { Box, Icon, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import React, { ReactNode } from 'react';

import DrawerLink from '../../components/common/drawers/ModuleDrawer/DrawerLink';
import { AttendanceStatus, EventStatus, eventBaseColor } from '../../types/Event';
import { UserConfigurableModule } from '../../types/User';
import { humanizeEventDateTimes, humanizeTimestamp } from '../../utility/dateUtils';
import { attendanceStatusColor } from '../../components/dashboard/EventsPanel/EventTile';
import {
  scheduleRowTileContainerSx,
  scheduleRowTileContentSx,
  scheduleRowTileHeaderLabelSx,
  scheduleRowTileHeaderSx,
  scheduleRowTileIndicatorSx,
  scheduleRowTileSecondaryIconSx,
  scheduleRowTileSecondaryLabelSx,
  scheduleRowTileSecondaryTextSx,
  scheduleRowTileTextContainerSx,
} from './employeesScheduleStyles';
import { IEventData } from './EmployeesSchedule';
import { DateTime } from 'luxon';
import { taskBaseColor } from '../../utility/task';
import { TaskStatus } from '../../types/Task';

interface IScheduleRowTileProps {
  eventWithAttendance: IEventData;
  dayCount: number;
}

interface StatusIndicatorConfig {
  title: EventStatus | AttendanceStatus;
  color: string;
  striped?: boolean;
}

// Returns the configuration for the status indicator based on the event status and attendance status.
const getStatusIndicatorConfig = (eventStatus: EventStatus, attendance: AttendanceStatus, theme: Theme): StatusIndicatorConfig => {
  switch (eventStatus) {
    case EventStatus.PLANNING:
    case EventStatus.SCHEDULED:
      return {
        title: eventStatus,
        color: eventBaseColor(EventStatus.PLANNING),
        striped: true,
      };

    case EventStatus.PUBLISHED:
    case EventStatus.IN_PROGRESS:
      return {
        title: attendance,
        color: attendanceStatusColor(attendance, theme),
      };

    case EventStatus.COMPLETE:
    case EventStatus.CANCELLED:
      return {
        title: eventStatus,
        color: theme.palette.common.black,
      };

    default:
      return {
        title: eventStatus,
        color: eventBaseColor(eventStatus),
      };
  }
};

export default function ScheduleRowTile({ eventWithAttendance, dayCount }: IScheduleRowTileProps) {
  const theme = useTheme();
  const event = eventWithAttendance.event;

  const indicator = getStatusIndicatorConfig(event.status, eventWithAttendance.attendanceStatus, theme);
  const dateLabel = humanizeEventDateTimes(event);

  const now = DateTime.now();
  const checkInCutoff = DateTime.fromISO(event.start_at).minus({ hours: 1 });

  let checkInMark: ReactNode = null;
  if (now >= checkInCutoff && dayCount < 5) {
    if (eventWithAttendance.checkedOut) {
      checkInMark = (<Icon sx={{ color: theme.palette.success.main }} title={`Checked out at ${humanizeTimestamp(eventWithAttendance.checkedOut)}`}><TaskAlt /></Icon>);
    } else if (eventWithAttendance.checkedIn) {
      checkInMark = (<Icon sx={{ color: taskBaseColor(TaskStatus.IN_PROGRESS) }} title={`Checked in at ${humanizeTimestamp(eventWithAttendance.checkedIn)}`}><Login /></Icon>);
    } else {
      checkInMark = (<Icon sx={{ color: theme.palette.warning.main }} title="Not checked in"><HourglassTop /></Icon>);
    }
  }

  return (
    <Box sx={scheduleRowTileContainerSx}>
      <Box
        title={indicator.title}
        aria-label={indicator.title}
        sx={scheduleRowTileIndicatorSx({
          color: indicator.color,
          stripeColor: theme.palette.divider,
          striped: indicator.striped,
        })}
      />

      <Box sx={scheduleRowTileContentSx}>

        <Box sx={scheduleRowTileHeaderSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: 'stretch', minWidth: 0 }}>
            <DrawerLink module={UserConfigurableModule.EVENTS} uuid={event.uuid} sx={{ width: 'fit-content' }}>
              <Typography title={event.name} sx={scheduleRowTileHeaderLabelSx}>
                {event.name}
              </Typography>
            </DrawerLink>
            {checkInMark}
          </Box>
        </Box>

        <Box sx={scheduleRowTileTextContainerSx}>
          <Box sx={scheduleRowTileSecondaryTextSx}>
            <CalendarMonthOutlined sx={scheduleRowTileSecondaryIconSx} />

            <Typography title={dateLabel} sx={scheduleRowTileSecondaryLabelSx}>
              {dateLabel}
            </Typography>
          </Box>

          <Box sx={scheduleRowTileSecondaryTextSx}>
            <AccountBalanceOutlined sx={scheduleRowTileSecondaryIconSx} />

            <DrawerLink module={UserConfigurableModule.ACCOUNTS} uuid={event.account_uuid}>
              <Typography title={event.account_display_name} sx={scheduleRowTileSecondaryLabelSx}>
                {event.account_display_name}
              </Typography>
            </DrawerLink>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
